import { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { appConfig } from "@/config/appConfig";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  FileText,
  Users,
  Calendar,
  CheckSquare,
  Home,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout, isSuperHR } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: Home, show: true },
    {
      path: "/hr",
      label: "HR Portal",
      icon: Users,
      show: user?.roles.includes("hr"),
    },
    {
      path: "/manager",
      label: "Approvals",
      icon: CheckSquare,
      show: user?.roles.includes("manager") || isSuperHR,
    },
    {
      path: "/employee",
      label: "My Leaves",
      icon: FileText,
      show: true,
    },
    { path: "/calendar", label: "Calendar", icon: Calendar, show: true },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-gradient-header backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-primary flex items-center justify-center">
              <Calendar className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {appConfig.orgName}
              </h1>
              <p className="text-xs text-muted-foreground">
                Leave Management System
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <button
                type="button"
                onClick={() => navigate("/account-settings")}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                {user?.firstName
                  ? `${user.firstName} ${user.lastName || ""}`
                  : user?.name}
              </button>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto py-2">
            {navItems
              .filter((item) => item.show)
              .map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.path ||
                  (item.path === "/hr" && location.pathname.startsWith("/hr/"));
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    onClick={() => navigate(item.path)}
                    className="whitespace-nowrap"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">{children}</main>
    </div>
  );
}
